import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { apiClient } from "@/services/api";
import type { TeamMemberRole, TeamRecordingsFilters, UpdateSessionRequest } from "@/types";

export function useTeams(enabled = true) {
  return useQuery({
    queryKey: ["teams"],
    queryFn: () => apiClient.getTeams(),
    enabled,
  });
}

export function useTeam(id: string) {
  return useQuery({
    queryKey: ["teams", id],
    queryFn: () => apiClient.getTeam(id),
    enabled: !!id,
  });
}

export function useTeamStats(teamId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["team-stats", teamId],
    queryFn: () => apiClient.getTeamStats(teamId),
    enabled: enabled && !!teamId,
  });
}

export type AddMemberErrorCode = "userNotFound" | "alreadyMember" | "unknown";

export class AddMemberError extends Error {
  code: AddMemberErrorCode;

  constructor(code: AddMemberErrorCode) {
    super(code);
    this.name = "AddMemberError";
    this.code = code;
  }
}

export function useAddTeamMemberByEmail(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      role,
    }: {
      email: string;
      role: TeamMemberRole;
    }) => {
      let userId: string;
      try {
        userId = (await apiClient.lookupUserByEmail(email)).id;
      } catch (error) {
        throw new AddMemberError(
          isAxiosError(error) && error.response?.status === 404
            ? "userNotFound"
            : "unknown",
        );
      }
      try {
        return await apiClient.addTeamMember(teamId, { userId, role });
      } catch (error) {
        throw new AddMemberError(
          isAxiosError<{ error?: { code?: string } }>(error) &&
          error.response?.data?.error?.code === "ALREADY_MEMBER"
            ? "alreadyMember"
            : "unknown",
        );
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      void queryClient.invalidateQueries({ queryKey: ["team-stats", teamId] });
    },
  });
}

export function useRemoveTeamMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => apiClient.removeTeamMember(teamId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      void queryClient.invalidateQueries({ queryKey: ["team-stats", teamId] });
    },
  });
}

export function useTeamRecordings(
  teamId: string,
  filters: TeamRecordingsFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: ["teams", teamId, "recordings", filters],
    queryFn: () => apiClient.getTeamRecordings(teamId, filters),
    enabled: enabled && !!teamId,
  });
}

export function useUpdateRecordingSession(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      data,
    }: {
      sessionId: string;
      data: UpdateSessionRequest;
    }) => apiClient.updateSession(sessionId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["teams", teamId, "recordings"],
      });
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
}
